/**
 * Videos & Channels by Subscriber Tier — Activity.
 *
 * THE COMPANION TO EsActivity.tsx, NOT A REPLACEMENT FOR IT. That panel
 * answers "is anything being written" in aggregate, across the whole video
 * and channel indices. This one asks the same question VidiqTiers already
 * asks of a different pipeline (Vidiq.tsx) — is EVERY subscriber tier's write
 * activity current, not just the whole table's — because the most valuable
 * channels and videos are also the fewest rows, and an aggregate hides
 * exactly the tier that has quietly stopped moving.
 *
 * A MANUAL-REFRESH SNAPSHOT, THE SAME PATTERN AS VidiqTiers, NOT A POLL LIKE
 * PipelineHealth: the underlying scan touches 442M video rows and 28M channel
 * rows, so this panel reads a cached snapshot by default and recomputes only
 * on an explicit click of Refresh. See `useTierActivityRefresh`'s own comment
 * in api/queries.ts for why a 409 from that click ("someone else is already
 * refreshing") is treated as "here is the current snapshot", never as a
 * failed panel.
 *
 * NULL IS NOT ZERO, restated for this panel's own shape of the same rule
 * every other snapshot panel on this board follows: `activity` being `{}`
 * (nothing computed yet) is a different, calmer state than a tier/window cell
 * genuinely reading `{inserted: 0, updated: 0}` — a real zero is an
 * actionable fact and must render as a visible "0", never as a blank cell or
 * a dash that would make it indistinguishable from "never computed".
 */
import { Fragment } from 'react'
import { useTierActivity, useTierActivityRefresh } from '../api/queries'
import { PanelState, Tile } from '../components/ui'
import { n, age } from '../components/format'
import type {
  TierActivity as TierActivityData,
  TierActivityRefresh,
} from '../api/schemas'

/** T0..T5, richest to poorest — the exact bands `collector/domain/entities.py`'s
 *  `Tier` enum defines (T0 >= 10M ... T5 < 1K/unknown), and the SAME labels the
 *  existing vidIQ-tier panel already shows for the equivalent bands (Vidiq.tsx's
 *  VidiqTiers table: '10M+', '1M-10M', '100K-1M', '10K-100K', '1K-10K',
 *  'under1K'). Reused here rather than reinvented, so an operator reading both
 *  panels sees one vocabulary for subscriber size. Falls back to the raw code
 *  for any tier the server sends that this map does not yet know about, rather
 *  than dropping the row. */
const TIER_LABELS: Record<string, string> = {
  T0: '10M+',
  T1: '1M-10M',
  T2: '100K-1M',
  T3: '10K-100K',
  T4: '1K-10K',
  T5: 'under1K',
}

/** The four trailing windows the endpoint always reports together — labelled
 *  the same way RangeChips labels them elsewhere on this board. Falls back to
 *  `${w}d` for any window this map does not know about, so a future added
 *  window still renders instead of vanishing. */
const WINDOW_LABELS: Record<number, string> = { 1: '1d', 2: '2d', 7: '7d', 30: '30d' }

const ENTITIES: Array<{ key: 'video' | 'channel'; label: string }> = [
  { key: 'video', label: 'videos' },
  { key: 'channel', label: 'channels' },
]

/** One entity's table — tier rows, window groups each holding its own
 *  inserted/updated pair. A nested header (window, then metric) rather than
 *  eight flat columns: with four windows × two metrics per entity, a flat
 *  header repeats "inserted"/"updated" four times with no grouping cue, while
 *  this layout states the window once and reads as one instrument per window. */
function EntityTable({ entityKey, label, tiers, windows, activity }: {
  entityKey: 'video' | 'channel'
  label: string
  tiers: string[]
  windows: number[]
  activity: TierActivityData['activity']
}) {
  const rows = activity[entityKey] ?? {}
  return (
    <div style={{ marginBottom: 18 }}>
      <div className="sec">
        <h2>{label}</h2>
        <span className="rule" />
      </div>
      <table className="t">
        <thead>
          <tr>
            <th rowSpan={2}>tier</th>
            {windows.map((w) => (
              <th key={w} colSpan={2} className="n">{WINDOW_LABELS[w] ?? `${w}d`}</th>
            ))}
          </tr>
          <tr>
            {windows.map((w) => (
              <Fragment key={w}>
                <th className="n q">inserted</th>
                <th className="n q">updated</th>
              </Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {tiers.map((tier) => {
            const tierRow = rows[tier]
            return (
              <tr key={tier}>
                <td>{TIER_LABELS[tier] ?? tier}</td>
                {windows.map((w) => {
                  // A missing cell (a tier/window this snapshot did not carry,
                  // e.g. a hostile/partial payload) reads as absent, never as
                  // a fabricated zero — the exact null-is-not-zero distinction
                  // this file's own module doc states. A genuine
                  // `{inserted: 0, ...}` still renders "0" via `n()`.
                  const counts = tierRow?.[String(w)]
                  return (
                    <Fragment key={w}>
                      <td className="n">{n(counts?.inserted ?? null)}</td>
                      <td className="n">{n(counts?.updated ?? null)}</td>
                    </Fragment>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}


/* Human phase names. The server sends the internal stage id; the panel says
 * what that stage is actually doing, because "videos" alone does not explain
 * why the bar has been on the same step for four minutes. */
const PHASE_LABEL: Record<string, string> = {
  channels: 'scanning channels',
  videos: 'scanning videos (the long one — 544M rows)',
  writing: 'writing the snapshot',
}

function secs(s: number): string {
  if (s < 60) return `${Math.round(s)}s`
  const m = Math.floor(s / 60)
  return `${m}m${String(Math.round(s % 60)).padStart(2, '0')}s`
}

/* Progress for a job with NO countable unit of work.
 *
 * The refresh is two long queries and a small write, so there is no honest
 * percentage of rows. What is real: which phase, how long this run has taken,
 * and how long the last one took. The bar is elapsed-vs-expected against that
 * history, and when there is no history it does not pretend — it shows an
 * indeterminate stripe and an elapsed counter instead of a number it made up. */
function RefreshProgress({ r }: { r: TierActivityRefresh }) {
  const determinate = r.pct != null
  return (
    <div style={{ marginBottom: 12 }} data-testid="tier-refresh-progress">
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        /* Omitted entirely when indeterminate — an aria-valuenow of 0 would
         * announce "0 percent" to a screen reader on a job that is running
         * fine and simply cannot be measured. */
        aria-valuenow={determinate ? Math.round(r.pct as number) : undefined}
        aria-label="tier activity refresh progress"
        style={{
          height: 6,
          borderRadius: 3,
          background: 'var(--line, #2a2a2a)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: determinate ? `${r.pct}%` : '35%',
            background: r.overrun ? 'var(--warning)' : 'var(--ok, #4a9)',
            transition: 'width 1s linear',
            ...(determinate
              ? {}
              : { animation: 'tierIndeterminate 1.6s ease-in-out infinite' }),
          }}
        />
      </div>
      <p className="q" style={{ marginTop: 6 }}>
        {PHASE_LABEL[r.phase] ?? r.phase} · step {r.phase_index} of{' '}
        {r.phase_count} · {secs(r.elapsed_s)} elapsed
        {r.overrun
          ? ` · longer than the last run (${secs(r.expected_s ?? 0)}) — still going`
          : r.expected_s != null
            ? ` of about ${secs(r.expected_s)}`
            : ' · first run, so there is no previous time to estimate from'}
      </p>
      <style>{
        '@keyframes tierIndeterminate{0%{margin-left:-35%}100%{margin-left:100%}}'
      }</style>
    </div>
  )
}

export function TierActivity() {
  const { data, isLoading, error } = useTierActivity()
  const refresh = useTierActivityRefresh()

  /* IN PROGRESS IF ANYONE IS REFRESHING, not only this tab. `refresh.isPending`
   * is true only for the click made here; `data.refresh.in_progress` is the
   * server's view and covers another user's click or another replica's run —
   * which is exactly the case the "already in progress" note below exists for,
   * and which previously showed no progress at all. */
  const serverRefresh = data?.refresh ?? null
  const inProgress = refresh.isPending || !!serverRefresh?.in_progress
  // The mutation resolved successfully in BOTH cases the 409 handling exists
  // to cover — a genuine fresh snapshot (data present) and "someone else's
  // refresh was already running" (data null, see useTierActivityRefresh's own
  // comment). Only the second needs a note; the first speaks for itself once
  // the table below updates.
  const concurrentRefreshNote = refresh.isSuccess && refresh.data === null

  /* THE SERVER DECLINED TO RECOMPUTE because a snapshot for today already
   * exists. Distinguished by the payload's own flag rather than by HTTP
   * status, because `post()` in api/client.ts discards the status code.
   * `=== false` and not `!x`: absent means "no opinion" (a GET, or a
   * collector predating the gate), which must not read as a cache hit. */
  const servedFromCache =
    refresh.isSuccess && refresh.data?.recompute_started === false

  return (
    <Tile
      title="Videos & Channels by Subscriber Tier — Activity"
      hint={
        !data?.captured_at
          ? 'never refreshed'
          : `snapshot ${age(data.age_s)} old${
              data.duration_ms ? ` · scan ${Math.round(data.duration_ms / 1000)}s` : ''}`
      }
      span
      action={
        <button className="btn" onClick={() => refresh.mutate()} disabled={inProgress}>
          {inProgress ? 'computing…' : 'refresh'}
        </button>
      }
    >
      <PanelState isLoading={isLoading} error={error}>
        {/* THE LAST BACKGROUND SCAN FAILED. Before this field existed a
            failed refresh was invisible everywhere but the collector's own
            stderr: the POST already answers 202, and a failure clears the
            lock/progress keys exactly as a success does, so the panel would
            otherwise keep showing the previous snapshot with no hint
            anything went wrong. Shown regardless of `inProgress` — it
            describes the LAST run, not necessarily the one happening now,
            and the wording says so. */}
        {data?.last_refresh_error ? (
          <p className="panel-err" style={{ marginBottom: 10 }}>
            Last refresh failed: {data.last_refresh_error}
          </p>
        ) : null}

        {inProgress ? (
          <>
            {serverRefresh ? <RefreshProgress r={serverRefresh} /> : null}
            <p className="q" style={{ marginBottom: 10 }}>
              Computing — this scans 544M video rows and 28M channel rows and
              takes about 35 minutes. It is safe to leave this tab open or
              close it; the scan runs on the server and the figures below
              update once it finishes.
            </p>
          </>
        ) : null}

        {!inProgress && concurrentRefreshNote ? (
          <p className="q" style={{ marginBottom: 10 }}>
            A refresh was already in progress — showing the latest available
            snapshot rather than starting a second one.
          </p>
        ) : null}

        {!inProgress && servedFromCache ? (
          <p className="q" style={{ marginBottom: 10 }}>
            Already computed today — showing that snapshot rather than paying
            for another 35-minute scan. Press Refresh tomorrow, or ask for a
            forced recompute, for newer figures.
          </p>
        ) : null}

        {/* EXACT SINCE 2026-08-18, and worth saying out loud because this
            panel spent its whole prior life showing estimates. The video half
            was a 0.01% page sample scaled 10,000x, which rendered 0 for every
            1d and 2d cell while ~29k videos were being inserted and ~48k
            updated per day — an all-zero short window on the one panel meant
            to catch a tier that has quietly stopped moving. */}
        {data?.captured_at && data?.video_estimated === false ? (
          <p className="q" style={{ marginBottom: 10 }}>
            Exact counts — every video and channel row in the window is
            counted, not sampled. The scan takes about 35 minutes, so it runs
            at most once a day and is cached; a second Refresh on the same day
            returns the cached figures.
          </p>
        ) : null}

        {/* VERSION SKEW, kept deliberately: a dashboard newer than its
            collector must degrade to an accurate label rather than assert a
            census over sampled numbers. */}
        {data?.captured_at && data?.video_estimated ? (
          <p className="q" style={{ marginBottom: 10 }}>
            This collector is still reporting sampled video figures — treat the
            video numbers as magnitudes and relative shares, not as counts.
            Channel figures are exact.
          </p>
        ) : null}

        {/* A SNAPSHOT THAT HAS NOT RUN IS NOT AN EMPTY PIPELINE, the same rule
            VidiqTiers states for its own identical case. Rendering a table of
            blanks here would read as "nothing has ever been written", which
            is very likely false — nobody has just pressed the button yet. */}
        {!data?.captured_at ? (
          <p className="q">
            No data yet — click Refresh to compute (this takes about 35
            minutes).
          </p>
        ) : (
          <>
            {data.stale ? (
              <p className="panel-err" style={{ marginBottom: 10 }}>
                Snapshot is {age(data.age_s)} old — older than{' '}
                {age(data.stale_after_seconds)}. Click Refresh to recompute
                before trusting the figures below.
              </p>
            ) : null}

            {ENTITIES.map(({ key, label }) => (
              <EntityTable key={key} entityKey={key} label={label}
                           tiers={data.tiers} windows={data.windows}
                           activity={data.activity} />
            ))}

            <p className="q" style={{ marginTop: 8 }}>
              A snapshot, not a live figure — this panel never polls. Press
              Refresh to recompute; the age above states how old what you are
              looking at actually is.
            </p>
          </>
        )}
      </PanelState>
    </Tile>
  )
}
